// 백트래킹 함수 정의
import { DomesticOrderDiscountSelectModalCoupon } from '~/api/converters/domestic/common/Coupons/SelectCouponModal/index.type'
import { MaxCouponDiscount } from '~/api/converters/domestic/order/Discount/index.type'
import { TMaxDiscountLimit } from '~/api/routes/domestic/order/controller/type'
import { ItemType } from '~/api/routes/domestic/order/types/shared'
import { getCouponApplyItemId } from '~/api/converters/domestic/order/Discount/CouponSelectModal'

export const TIME_OUT_MAX_DISCOUNT: MaxCouponDiscount = {
  isTimeout: true,
  discountAppliedItems: [],
  maxDiscount: 0,
}

// 쿠폰 id 와 쿠폰 사용 회수 객체를 전달받으면 쿠폰 사용 회수를 증가시킨다.
const increaseCouponUseCount = (id: string, stock: Map<string, number>) => {
  stock.set(id, (stock.get(id) ?? 0) + 1)
}

// 주어진 쿠폰 배열 중 재고가 충분하고 가장 큰 할인 금액을 갖는 쿠폰의 재고를 차감하고 금액을 반환
const getMaxAmountCouponAndCheckStock = (
  coupons: { id: string; price: number; remainCount: number }[],
  futureCouponStocks: Map<string, number>
) => {
  const { id, price } = coupons
    // 재고 확인
    .filter(coupon => coupon.remainCount - (futureCouponStocks.get(coupon.id) ?? 0) > 0)
    // 최대값을 갖는 쿠폰 반환
    .reduce((acc, coupon) => (coupon.price > acc.price ? coupon : acc), {
      id: '',
      price: 0,
    })

  if (id) {
    increaseCouponUseCount(id, futureCouponStocks)
  }

  return price
}

const getFutureMax = <Coupon extends { allowAdditional?: boolean }>({
  items,
  currentIndex,
  mapCoupon,
  futureCouponStocks,
}: {
  items: {
    price: number | null
    coupons: Coupon[]
    additionalOneCoupons: Coupon[]
    additionalTwoCoupons: Coupon[]
  }[]
  currentIndex: number
  mapCoupon: (coupon: Coupon | null) => { id: string; price: number; remainCount: number }
  futureCouponStocks: Map<string, number>
}) => {
  //  앞으로 조회할 상품의 재고를 쉽게 체크하기 위해 mapCoupon 을 이용하여 쿠폰 정보를 변환
  const futureItems = items.slice(currentIndex).map(item => {
    const { coupons, additionalOneCoupons, additionalTwoCoupons, price } = item
    const futureCoupons: {
      id: string
      price: number
      remainCount: number
      allowAdditional: boolean
    }[] = coupons.map(coupon => {
      const mappedCoupon = mapCoupon(coupon)
      return {
        ...mappedCoupon,
        allowAdditional: coupon.allowAdditional ?? false,
      }
    })
    const futureAdditionalOneCoupons = additionalOneCoupons.map(mapCoupon)
    const futureAdditionalTwoCoupons = additionalTwoCoupons.map(mapCoupon)

    return {
      price: price ?? 0,
      coupons: futureCoupons,
      additionalOneCoupons: futureAdditionalOneCoupons,
      additionalTwoCoupons: futureAdditionalTwoCoupons,
    }
  })

  // 앞으로 조회할 상품들에 대한 쿠폰을 순회하며 최대 할인 금액을 계산
  // futureMax 는 모든 경우의 수를 고려하지 않고  n^2 만큼만 순회 후 최대값을 반환하지만,
  // 이 정도만 해주어도 충분히 최적화된 결과를 얻을 수 있었음.
  // 추후 더 좋은 백트래킹 알고리즘을 찾는다면 수정이 필요
  const futureMax = futureItems.reduce((acc, cur) => {
    const { coupons, additionalOneCoupons, additionalTwoCoupons, price } = cur

    // 중복 허용 가능한 쿠폰 중 최대값
    const maxAmountOfAllowedAdditionalCoupons = getMaxAmountCouponAndCheckStock(
      coupons.filter(coupon => coupon.allowAdditional),
      futureCouponStocks
    )

    // 중복 허용 불가한 쿠폰 중 최대값
    const maxAmountOfDisallowedAdditionalCoupons = getMaxAmountCouponAndCheckStock(
      coupons.filter(coupon => !coupon.allowAdditional),
      futureCouponStocks
    )

    // 더하기 쿠폰 1 최대값
    const maxAmountOfAdditionalOneCoupons = getMaxAmountCouponAndCheckStock(
      additionalOneCoupons,
      futureCouponStocks
    )

    // 더하기 쿠폰 2 최대값을 갖는 쿠폰 적용
    const maxAmountOfAdditionalTwoCoupons = getMaxAmountCouponAndCheckStock(
      additionalTwoCoupons,
      futureCouponStocks
    )

    // max( 중복허용 불가 쿠폰 최대값, 중복허용 가능 쿠폰 최대값 + 더하기 1,2 최대값)
    const maxTotalCouponAmount = Math.max(
      maxAmountOfDisallowedAdditionalCoupons,
      maxAmountOfAllowedAdditionalCoupons +
        maxAmountOfAdditionalOneCoupons +
        maxAmountOfAdditionalTwoCoupons
    )

    // min( 상품 가격, 쿠폰 최대값) : 쿠폰 최대 적용값은 상품 가격을 넘을 수 없음
    return Math.min(price, maxTotalCouponAmount) + acc
  }, 0)

  return futureMax
}

const getMaxCouponDiscount = <Coupon extends { allowAdditional?: boolean }>({
  items,
  filterCoupon,
  mapCoupon,
  mapCouponDiscountAppliedItem,
  maxDiscountLimit,
}: {
  maxDiscountLimit: TMaxDiscountLimit
  items: {
    typeIdx: number
    coupons: Coupon[]
    price: number | null
    additionalOneCoupons: Coupon[]
    additionalTwoCoupons: Coupon[]
    type: ItemType
  }[]
  filterCoupon: (coupon: Coupon | null) => boolean
  mapCoupon: (coupon: Coupon | null) => { id: string; price: number; remainCount: number }
  mapCouponDiscountAppliedItem: (
    coupon: Coupon,
    currentIndex: number
  ) => DomesticOrderDiscountSelectModalCoupon
}): MaxCouponDiscount => {
  const { timeout } = maxDiscountLimit
  let maxDiscount = 0 // 최대 할인액을 저장하는 변수
  let maxDiscountAppliedItems: MaxCouponDiscount['discountAppliedItems'] = [] // 최대 할인액이 적용된 property 를 저장하는 배열
  const couponStocks = new Map() // 각 쿠폰의 재고를 관리하는 객체 (key: 쿠폰 id, value: 사용한 횟수)
  const now = Date.now()
  let isTimeout = false

  const backtrack = (
    currentIndex: number,
    currentDiscount: number,
    discountedItems: MaxCouponDiscount['discountAppliedItems']
  ) => {
    // 모든 상품을 조회 후, 최대 할인 금액, 쿠폰 적용된 숙소를 업데이트
    if (currentIndex === items.length) {
      if (maxDiscount < currentDiscount) {
        maxDiscount = currentDiscount
        maxDiscountAppliedItems = discountedItems
      }
      return
    }

    /**
     * backtrack 조건: 현재 할인 금액과 나머지 상품에 적용가능한 최대 할인 금액의 합이 기존 최대 할인 금액보다 작은 경우, 탐색 중단
     */
    const futureCouponStocks = new Map(couponStocks.entries())
    const futureMax = getFutureMax({
      items,
      currentIndex,
      mapCoupon,
      futureCouponStocks,
    })

    //  현재 할인 금액과 나머지 상품에 적용가능한 최대 할인 금액의 합이 기존 최대 할인 금액보다 작은 경우, 탐색 중단
    if (currentDiscount + futureMax <= maxDiscount && currentIndex > 0) {
      return
    }

    // 현재 상품 index 에서 순회할
    const { allowedCoupons, disallowedCoupons } = items[currentIndex].coupons.reduce<{
      allowedCoupons: (Coupon | null)[]
      disallowedCoupons: (Coupon | null)[]
    }>(
      (acc, coupon) => {
        // 쿠폰 플랫폼 쿠폰은 allowAdditional 가 undefined 이므로 명시적으로 false 와 비교
        if (coupon.allowAdditional === false) {
          acc.disallowedCoupons.push(coupon)
        } else {
          acc.allowedCoupons.push(coupon)
        }
        return acc
      },
      { allowedCoupons: [], disallowedCoupons: [] }
    )

    // 재고 확인 후 다음 단계 탐색
    const checkStockAndSearch = (
      mainCoupon: Coupon | null,
      additionalOneCoupon: Coupon | null,
      additionalTwoCoupon: Coupon | null
    ) => {
      const { id: mainCouponId, price: mainCouponPrice } = mapCoupon(mainCoupon)
      const { id: additionalOneCouponId, price: additionalOneCouponPrice } =
        mapCoupon(additionalOneCoupon)
      const { id: additionalTwoCouponId, price: additionalTwoCouponPrice } =
        mapCoupon(additionalTwoCoupon)

      // 쿠폰 적용 가능 여부 판단
      // (쿠폰이 null OR null 이 아닌 경우 재고를 체크)
      // AND
      // 쿠폰 가격 <= 상품 가격
      const getIsCouponStockAvailable = (coupon: Coupon | null, stock: Map<string, number>) => {
        const { id, remainCount } = mapCoupon(coupon)
        const stockCount = stock.get(id)
        return !id || (!!id && (!stockCount || stockCount < remainCount))
      }

      const isMainCouponStockAvailable = getIsCouponStockAvailable(mainCoupon, couponStocks)
      const isAdditionalOneCouponStockAvailable = getIsCouponStockAvailable(
        additionalOneCoupon,
        couponStocks
      )
      const isAdditionalTwoCouponStockAvailable = getIsCouponStockAvailable(
        additionalTwoCoupon,
        couponStocks
      )
      const isCouponPriceLessOrSameThanItemPrice =
        mainCouponPrice + additionalOneCouponPrice + additionalTwoCouponPrice <=
        (items[currentIndex].price ?? 0)

      if (
        isMainCouponStockAvailable &&
        isAdditionalOneCouponStockAvailable &&
        isAdditionalTwoCouponStockAvailable &&
        isCouponPriceLessOrSameThanItemPrice
      ) {
        // 쿠폰이 null 이 아닌 경우에만 재고 차감
        if (mainCoupon) {
          increaseCouponUseCount(mainCouponId, couponStocks)
        }
        if (additionalOneCoupon) {
          increaseCouponUseCount(additionalOneCouponId, couponStocks)
        }
        if (additionalTwoCoupon) {
          increaseCouponUseCount(additionalTwoCouponId, couponStocks)
        }

        const discountTotal = mainCouponPrice + additionalOneCouponPrice + additionalTwoCouponPrice

        const typeIdx = items[currentIndex].typeIdx
        const type = items[currentIndex].type
        const couponApplyItemId = getCouponApplyItemId(type, typeIdx)
        backtrack(
          currentIndex + 1,
          currentDiscount + discountTotal,
          discountedItems.concat({
            typeIdx,
            couponApplyItemId,
            appliedCoupon:
              mainCoupon && mapCouponDiscountAppliedItem(mainCoupon, items[currentIndex].typeIdx),
            appliedAdditionalOneCoupon:
              additionalOneCoupon &&
              mapCouponDiscountAppliedItem(additionalOneCoupon, items[currentIndex].typeIdx),
            appliedAdditionalTwoCoupon:
              additionalTwoCoupon &&
              mapCouponDiscountAppliedItem(additionalTwoCoupon, items[currentIndex].typeIdx),
          })
        )

        // 쿠폰이 null 이 아닌 경우에만 재고 복구
        if (mainCoupon) {
          couponStocks.set(mainCouponId, couponStocks.get(mainCouponId) - 1)
        }
        if (additionalOneCoupon) {
          couponStocks.set(additionalOneCouponId, couponStocks.get(additionalOneCouponId) - 1)
        }
        if (additionalTwoCoupon) {
          couponStocks.set(additionalTwoCouponId, couponStocks.get(additionalTwoCouponId) - 1)
        }
      }
    }

    // 가능한 모든 조합 순회
    // '선택 안함' 경우를 포함시키기 위해 null 추가
    // 더하기 쿠폰의 쿠폰 적용 우선순위가 더 높으므로 더하기 쿠폰을 먼저 순회
    const filteredAllowedCoupons = [...allowedCoupons, null].filter(filterCoupon)
    const filteredAdditionalOneCoupons = [...items[currentIndex].additionalOneCoupons, null].filter(
      filterCoupon
    )
    const filteredAdditionalTwoCoupons = [...items[currentIndex].additionalTwoCoupons, null].filter(
      filterCoupon
    )

    filteredAdditionalOneCoupons.forEach(additionalOneCoupon => {
      filteredAdditionalTwoCoupons.forEach(additionalTwoCoupon => {
        filteredAllowedCoupons.forEach(mainCoupon => {
          const backTrackNow = Date.now()
          if (backTrackNow - now > timeout) {
            isTimeout = true
            return
          }
          checkStockAndSearch(mainCoupon, additionalOneCoupon, additionalTwoCoupon)
        })
      })
    })
    disallowedCoupons.filter(filterCoupon).forEach(mainCoupon => {
      const backTrackNow = Date.now()
      if (backTrackNow - now > timeout) {
        isTimeout = true
        return
      }
      checkStockAndSearch(mainCoupon, null, null)
    })
  }
  backtrack(0, 0, [])

  if (isTimeout) {
    return TIME_OUT_MAX_DISCOUNT
  }

  return {
    discountAppliedItems: maxDiscountAppliedItems,
    maxDiscount,
  }
}

export default getMaxCouponDiscount
